/**
 * New-module wizard modal. Extracted verbatim (behavior-preserving) from the
 * `{isNewModuleMenuOpen && (…)}` JSX block in layout/Sidebar.tsx.
 *
 * The orchestrator still gates rendering on `isNewModuleMenuOpen` and owns all
 * the wizard state (step/name/error/search), the `isCreatingModuleRef`
 * double-submit guard inside `handleCreateModule`, and the focus effect that
 * targets `newModuleNameInputRef`. This component is purely presentational: it
 * renders the two-step wizard and calls the threaded-in setters/handler. The
 * inline name-validation ternaries are preserved exactly as in the original
 * (they intentionally stay inline rather than becoming a shared helper).
 */

import type { RefObject } from 'react';
import { canSeeClients } from '@/modules/contacts/clients/clientsFlag';
import { isDriveHiddenModuleType } from '@/lib/drive-deployment';
import { NEW_MODULE_CORE_CATALOG, NEW_MODULE_PLUGIN_CATALOG } from '../constants';
import type { SidebarModuleItem } from '../types';

type NewModuleStep = { type: string; label: string; emoji: string } | null;

interface NewModuleModalProps {
  newModuleStep: NewModuleStep;
  setNewModuleStep: (value: NewModuleStep) => void;
  newModuleName: string;
  setNewModuleName: (value: string) => void;
  newModuleNameError: string;
  setNewModuleNameError: (value: string) => void;
  newModuleSearch: string;
  setNewModuleSearch: (value: string) => void;
  isCreatingModule: boolean;
  setIsNewModuleMenuOpen: (value: boolean) => void;
  newModuleNameInputRef: RefObject<HTMLInputElement | null>;
  sidebarModuleItems: SidebarModuleItem[];
  profileEmail: string | undefined;
  handleCreateModule: (type: string, label: string) => void;
}

export function NewModuleModal({
  newModuleStep,
  setNewModuleStep,
  newModuleName,
  setNewModuleName,
  newModuleNameError,
  setNewModuleNameError,
  newModuleSearch,
  setNewModuleSearch,
  isCreatingModule,
  setIsNewModuleMenuOpen,
  newModuleNameInputRef,
  sidebarModuleItems,
  profileEmail,
  handleCreateModule,
}: NewModuleModalProps) {
  return (
    <div className="new-module-modal-backdrop" onClick={() => { setIsNewModuleMenuOpen(false); setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); setNewModuleSearch(''); }}>
      <div className="new-module-modal" onClick={(e) => e.stopPropagation()}>
        {!newModuleStep ? (
          <>
            <div className="new-module-modal-header">
              <h2 className="new-module-modal-title">New Module</h2>
              <button className="new-module-modal-close" onClick={() => { setIsNewModuleMenuOpen(false); setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); setNewModuleSearch(''); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ padding: '0 16px 8px' }}>
              <input
                type="text"
                placeholder="Search modules..."
                value={newModuleSearch}
                onChange={(e) => setNewModuleSearch(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>
            <div className="new-module-modal-body">
              <div className="new-module-section">
                <div className="new-module-section-label">Core</div>
                <div className="new-module-grid">
                  {NEW_MODULE_CORE_CATALOG.filter((mod) => !isDriveHiddenModuleType(mod.type)).filter((mod) => mod.type !== 'client_list' || canSeeClients(profileEmail)).filter((mod) => !newModuleSearch || mod.label.toLowerCase().includes(newModuleSearch.toLowerCase()) || mod.desc.toLowerCase().includes(newModuleSearch.toLowerCase())).map((mod) => (
                    <button key={mod.type} className="new-module-card" onClick={() => { setNewModuleStep({ type: mod.type, label: mod.label, emoji: mod.emoji }); setNewModuleName(mod.label); setNewModuleNameError(''); }}>
                      <span className="new-module-card-emoji">{mod.emoji}</span>
                      <span className="new-module-card-label">{mod.label}</span>
                      <span className="new-module-card-desc">{mod.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="new-module-section">
                <div className="new-module-section-label">Plugins</div>
                <div className="new-module-grid">
                  {NEW_MODULE_PLUGIN_CATALOG.filter((mod) => !isDriveHiddenModuleType(mod.type)).filter((mod) => !newModuleSearch || mod.label.toLowerCase().includes(newModuleSearch.toLowerCase()) || mod.desc.toLowerCase().includes(newModuleSearch.toLowerCase())).map((mod) => (
                    <button key={mod.type} className="new-module-card" onClick={() => { setNewModuleStep({ type: mod.type, label: mod.label, emoji: mod.emoji }); setNewModuleName(mod.label); setNewModuleNameError(''); }}>
                      <span className="new-module-card-emoji">{mod.emoji}</span>
                      <span className="new-module-card-label">{mod.label}</span>
                      <span className="new-module-card-desc">{mod.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="new-module-modal-header">
              <h2 className="new-module-modal-title">
                <span className="new-module-card-emoji">{newModuleStep.emoji}</span>
                Name your {newModuleStep.label} module
              </h2>
              <button className="new-module-modal-close" onClick={() => { setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="new-module-modal-body">
              <input
                ref={newModuleNameInputRef}
                className={`new-module-name-input${newModuleNameError ? ' error' : ''}`}
                type="text"
                value={newModuleName}
                disabled={isCreatingModule}
                onChange={(e) => { setNewModuleName(e.target.value); setNewModuleNameError(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isCreatingModule) {
                    const name = newModuleName.trim();
                    if (!name) { setNewModuleNameError('Name is required'); return; }
                    if (sidebarModuleItems.some((item) => (item.displayName || '').toLowerCase() === name.toLowerCase())) { setNewModuleNameError('A module with this name already exists'); return; }
                    handleCreateModule(newModuleStep.type, name);
                  }
                  if (e.key === 'Escape') { setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); }
                }}
                placeholder={newModuleStep.label}
              />
              {newModuleNameError && <div className="new-module-name-error">{newModuleNameError}</div>}
              <div className="new-module-name-actions">
                <button className="new-module-btn-back" disabled={isCreatingModule} onClick={() => { setNewModuleStep(null); setNewModuleName(''); setNewModuleNameError(''); }}>Back</button>
                <button className="new-module-btn-create" disabled={isCreatingModule} onClick={() => {
                  const name = newModuleName.trim();
                  if (!name) { setNewModuleNameError('Name is required'); return; }
                  if (sidebarModuleItems.some((item) => (item.displayName || '').toLowerCase() === name.toLowerCase())) { setNewModuleNameError('A module with this name already exists'); return; }
                  handleCreateModule(newModuleStep.type, name);
                }}>{isCreatingModule ? 'Creating...' : 'Create'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
